import { LiveAnnouncer } from '@angular/cdk/a11y';
import { inject, Injectable } from '@angular/core';
import { Store, Action } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';

import {
  catchError,
  combineLatest,
  filter,
  map,
  Observable,
  of,
  pipe,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { captureException } from '@sentry/browser';
import { selectLoadedProjectId } from '@core/state/ui/project/project-ui.selectors';
import { getSendableStudyTableParamsDto } from '../../state/ui/study/study-ui.selectors';
import { studySchema } from '@core/state/entities/study/study.entity';
import { selectCurrentProject } from '@app/core/state/entities/project/project.selectors';
import { StudiesTableHttpService } from '@core/services/study-table/study-table.service';
import {
  ApiException,
  StudyHttpService,
} from '@core/services/api-client.generated';
import { IncomingTableParamsDtoOfStudyFilters } from '../api-client.generated';
import { studiesActions } from '@core/actions/studies.actions';
import { projectDetailActions } from '@core/actions/project-detail.actions';
import { selectStudyListOnScreen } from '@core/state/ui/study/study-ui.selectors';

export const projectStudyIncomingDtoSchema = {
  data: [studySchema],
};

/** The problem type the server answers a refused PDF filter with (#4719, `409`). */
export const PDF_STATUS_FILTER_UNAVAILABLE = 'pdf-status-filter-unavailable';

/** What the reader hears when the server refuses the PDF filter (open question 4, settled). */
export const PDF_STATUS_FILTER_REFUSED_MESSAGE =
  "PDF status isn't available, so the PDF filter is off.";

/**
 * Whether a failed list fetch is the server refusing the PDF filter: the generated client throws the problem document
 * it declares for `409`, or an `ApiException` carrying it.
 */
export function isPdfStatusFilterRefusal(error: unknown): boolean {
  let problem: unknown = error;
  if (ApiException.isApiException(error)) {
    if (error.status !== 409) return false;
    try {
      problem = JSON.parse(error.response);
    } catch {
      return false;
    }
  }
  return (
    typeof problem === 'object' &&
    problem !== null &&
    (problem as { type?: unknown }).type === PDF_STATUS_FILTER_UNAVAILABLE
  );
}

export const combineWithNext = <TOriginal, TIncoming>(
  combineWithObservable$: Observable<TIncoming>
) =>
  pipe(
    switchMap((originalItem: TOriginal) =>
      combineLatest([of(originalItem).pipe(take(1)), combineWithObservable$])
    )
  );

@Injectable()
export class StudyTableEffects {
  studyPageData$: Observable<Action> = createEffect(() => {
    return this._actions$.pipe(
      ofType(studiesActions.fetchStudyList, studiesActions.refreshStudyList),
      combineWithNext(
        this._store
          .select(selectLoadedProjectId)
          .pipe(filter((pId): pId is string => !!pId))
      ),
      concatLatestFrom((x) =>
        this._store.select(getSendableStudyTableParamsDto)
      ),
      switchMap(
        ([[, projectId], sendableParams]: [
          [any, string],
          IncomingTableParamsDtoOfStudyFilters,
        ]) => {
          return this._studyService
            .getTableData(projectId, sendableParams)
            .pipe(
              map((pagedStudyDetails) =>
                studiesActions.studyListUpdated(pagedStudyDetails)
              ),
              // The Library shows the failure in the page, with "Try again" (C-12); no snackbar, so there is one
              // message. The effect keeps listening for the next fetch.
              catchError((err: unknown) => {
                // #4719: the PDF filter isn't available after all. Reset it to Any and hide it; the URL rewrite that
                // follows fetches the list once more.
                if (isPdfStatusFilterRefusal(err)) {
                  return of(studiesActions.pdfStatusFilterRefused());
                }
                captureException(
                  `An error occurred while fetching studies. - ${JSON.stringify(err)}`
                );
                return of(studiesActions.studyListLoadFailed());
              })
            );
        }
      )
    );
  });

  /**
   * #4719 criterion 13: the list has no live region of its own, so the refusal is announced politely through the CDK
   * announcer (D16).
   */
  announcePdfStatusFilterRefused$ = createEffect(
    () => {
      return this._actions$.pipe(
        ofType(studiesActions.pdfStatusFilterRefused),
        tap(() => {
          void this._announcer.announce(
            PDF_STATUS_FILTER_REFUSED_MESSAGE,
            'polite'
          );
        })
      );
    },
    { dispatch: false }
  );

  /**
   * Reversible deletion S4a (DL-R1S-10): when a search of the open project is deleted, a study list already on screen
   * is fetched again with its current parameters, so its rows and totals drop the moved Studies without a reload. A
   * list that is not loaded is left alone: opening it fetches afresh.
   */
  refreshAfterSearchDeleted$: Observable<Action> = createEffect(() => {
    return this._actions$.pipe(
      ofType(projectDetailActions.searchDeletedNotificationReceived),
      concatLatestFrom(() => this._store.select(selectStudyListOnScreen)),
      filter(([, onScreen]) => onScreen),
      map(() => studiesActions.refreshStudyList())
    );
  });

  private readonly _announcer = inject(LiveAnnouncer);

  constructor(
    private _store: Store,
    private _actions$: Actions,
    private _studiesTableService: StudiesTableHttpService,
    private _studyService: StudyHttpService
  ) {}
}
